import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, TrustPill } from "@/components/dash/ui";

export default function BoardPackTrustPage() {
  const q = useQuery({ queryKey: ["trust-board"], queryFn: dashApi.trustBoardPack });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Board Pack Trust Appendix"
        description="Which board-pack figures can be trusted, and which depend on estimated, partial, stale or conflicting data. This is the same appendix included in the generated Board Pack."
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : d && (
        <>
          <Panel title="Source freshness">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Source</th><th className="pb-2 font-medium">Trust</th>
                <th className="pb-2 font-medium">Status</th><th className="pb-2 text-right font-medium">Last data</th>
              </tr></thead>
              <tbody className="divide-y divide-ink-500/40">
                {d.freshness.map((f) => (
                  <tr key={f.source} className="text-slate-300">
                    <td className="py-2 text-slate-100">{f.name}</td>
                    <td className="py-2"><TrustPill label={f.label} /></td>
                    <td className="py-2 text-slate-400">{f.status}</td>
                    <td className="py-2 text-right text-[11px] text-slate-500">{f.freshness ? fmtRelative(f.freshness) : "never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Estimated / partial data">
              {!d.estimated.length ? <EmptyState compact>No estimated-data dependencies flagged.</EmptyState> : (
                <ul className="space-y-2">{d.estimated.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-2.5"><TrustPill label={i.label} /><p className="text-xs text-slate-400">{i.reason}</p></li>
                ))}</ul>
              )}
            </Panel>
            <Panel title="Missing / insufficient data">
              {!d.missing.length ? <EmptyState compact>No missing/stale data detected.</EmptyState> : (
                <ul className="space-y-2">{d.missing.map((i, idx) => (
                  <li key={idx} className="flex items-start gap-2.5"><StatusPill status={i.severity} /><p className="text-xs text-slate-400">{i.reason}</p></li>
                ))}</ul>
              )}
            </Panel>
          </div>

          <Panel title="Reconciliation warnings">
            {!d.reconciliation_warnings.length ? <EmptyState compact>No reconciliation conflicts or unresolved possible-matches.</EmptyState> : (
              <ul className="space-y-2">{d.reconciliation_warnings.map((w, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span className="text-[11px] uppercase text-amber-300">{w.status}</span>
                  <p className="text-xs text-slate-400">{w.source_a} vs {w.source_b}: {w.explanation}</p>
                </li>
              ))}</ul>
            )}
          </Panel>

          <Panel title="KPI trust">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-ink-500/40">
                {d.kpi_trust.map((k) => (
                  <tr key={k.kpi} className="text-slate-300">
                    <td className="py-2 text-slate-200">{k.kpi}</td>
                    <td className="py-2"><TrustPill label={k.label} /></td>
                    <td className="py-2 text-xs text-slate-500">{k.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <p className="text-xs text-slate-500">{d.repair_revenue_note}</p>
        </>
      )}
    </div>
  );
}
